import React, { useEffect } from "react";
import { navigate } from "@reach/router";
import { Header } from "../../internal/Header/Header";
import labels from "../../../helpers/labels.json";
import { useLocation } from "@reach/router";
import { getPathAfterCaseManager } from "../../../helpers/functions";
import useUser from "../../../hooks/useUser";

const BASE_PATH = "/dashboard/case-manager";

const MODULE_ORDER = [
  { key: "pendingActions", segment: "" },
  { key: "proformaInvoice", segment: "/proforma-invoice" },
  { key: "upload", segment: "/uploads" },
  { key: "verifyParties", segment: "/verify-parties" },
  { key: "bulkNotice", segment: "/bulk-notice" },
  { key: "documentReports", segment: "/document-reports" },

  { key: "negotiation", segment: "/negotiation" },
  { key: "mediation", segment: "/mediation" },
  { key: "arbitration", segment: "/arbitration" },
  { key: "bulkUploads", segment: "/bulk-uploads" },

  { key: "parties", segment: "/parties" },
  { key: "commsManager", segment: "/comms-manager" },
  { key: "profiles", segment: "/profiles" },
  { key: "payments", segment: "/payments" },

  { key: "meetings", segment: "/generic-meeting" },

  { key: "transcriber", segment: "/transcriber" },
  { key: "misDashboard", segment: "/mis-dashboard" },
  { key: "fieldMapping", segment: "/field-mapping" },
  { key: "managementTools", segment: "/tools" },
  { key: "finalApprover", segment: "/final-approver" },
  { key: "esigner", segment: "/esigner" },
  { key: "noticeTracking", segment: "/tracking" },
  { key: "hasBookingAccess", segment: "/booking-dashboard" },
  { key: "pricing", segment: "/pricing" },
];

const SEGMENT_TO_MODULE_KEY = {
  "proforma-invoice": "proformaInvoice",
  uploads: "upload",
  "verify-parties": "verifyParties",
  "bulk-notice": "bulkNotice",
  "document-reports": "documentReports",
  negotiation: "negotiation",
  mediation: "mediation",
  arbitration: "arbitration",
  "bulk-uploads": "bulkUploads",
  parties: "parties",
  "comms-manager": "commsManager",
  profiles: "profiles",
  payments: "payments",
  "generic-meeting": "meetings",
  "meeting-details": "meetings",
  "daily-cause": "meetings",
  "neutral-meeting-details": "meetings",
  transcriber: "transcriber",
  "mis-dashboard": "misDashboard",
  "field-mapping": "fieldMapping",
  tools: "managementTools",
  "final-approver": "finalApprover",
  esigner: "esigner",
  tracking: "noticeTracking",
  "booking-dashboard": "hasBookingAccess",
  pricing: "pricing",
};

const hasAccess = (moduleAccess, key) => {
  const value = moduleAccess?.[key];
  return value === 1 || value === true;
};

const normalize = (path) => (path || "").replace(/\/+$/, "") || "/";

const getFirstAccessiblePath = (moduleAccess) => {
  const match = MODULE_ORDER.find((m) => hasAccess(moduleAccess, m.key));
  if (!match) return null;
  return `${BASE_PATH}${match.segment}`;
};

export default function MainRoute({ showSidebar = true, children }) {
  const { userObject } = useUser();
  const location = useLocation();
  const moduleAccess = userObject?.moduleAccess || {};

  const currentSegment = getPathAfterCaseManager(location?.pathname);

  useEffect(() => {
    if (!userObject || !userObject.moduleAccess) return;

    const moduleKeyForCurrentRoute = currentSegment
      ? SEGMENT_TO_MODULE_KEY[currentSegment]
      : "pendingActions";

    if (!moduleKeyForCurrentRoute) return;

    const currentRouteIsAccessible = hasAccess(
      moduleAccess,
      moduleKeyForCurrentRoute,
    );

    if (currentRouteIsAccessible) return;

    const fallbackPath = getFirstAccessiblePath(moduleAccess);
    const currentPath = normalize(location.pathname);

    if (fallbackPath && normalize(fallbackPath) !== currentPath) {
      navigate(fallbackPath, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userObject, location.pathname]);

  const selectedSubItem =
    currentSegment === "proforma-invoice"
      ? labels.proforma_invoice
      : currentSegment === "uploads"
        ? labels.upload
        : currentSegment === "verify-parties"
          ? labels.verify
          : currentSegment === "bulk-notice"
            ? labels.bulk_notice
            : currentSegment === "document-reports"
              ? labels.document_reports
              : currentSegment === "negotiation"
                ? labels.negotiation
                : currentSegment === "mediation"
                  ? labels.mediation
                  : currentSegment === "arbitration"
                    ? labels.arbitration
                    : currentSegment === "bulk-uploads"
                      ? labels.bulk_uploads
                      : currentSegment === "parties"
                        ? labels.parties
                        : currentSegment === "comms-manager"
                          ? labels.comms_manager
                          : currentSegment === "profiles"
                            ? labels.profiles
                            : currentSegment === "payments"
                              ? labels.payments
                              : currentSegment === "generic-meeting"
                                ? labels.generic_meeting
                                : currentSegment === "meeting-details"
                                  ? labels.meeting_details
                                  : currentSegment === "daily-cause"
                                    ? labels.daily_casuse
                                    : currentSegment ===
                                        "neutral-meeting-details"
                                      ? labels.neutral_meeting
                                      : location.pathname.includes(BASE_PATH)
                                        ? labels.pending_actions
                                        : "";

  const selectedItem =
    currentSegment === "proforma-invoice" ||
    currentSegment === "uploads" ||
    currentSegment === "verify-parties" ||
    currentSegment === "bulk-notice" ||
    currentSegment === "document-reports"
      ? labels.my_tasks
      : currentSegment === "generic-meeting" ||
          currentSegment === "meeting-details" ||
          currentSegment === "daily-cause" ||
          currentSegment === "neutral-meeting-details"
        ? labels.meetings
        : currentSegment === "negotiation" ||
            currentSegment === "mediation" ||
            currentSegment === "arbitration" ||
            currentSegment === "bulk-uploads"
          ? labels.cases
          : currentSegment === "parties"
            ? labels.parties
            : currentSegment === "comms-manager"
              ? labels.comms_manager
              : currentSegment === "profiles"
                ? labels.profiles
                : currentSegment === "payments"
                  ? labels.payments
                  : currentSegment === "transcriber"
                    ? labels.transcriber
                    : currentSegment === "mis-dashboard"
                      ? labels.mis_dashboard
                      : currentSegment === "tools"
                        ? labels.management_tools
                        : currentSegment === "final-approver"
                          ? labels.final_approver
                          : currentSegment === "esigner"
                            ? labels.esigner
                            : currentSegment === "tracking"
                              ? labels.notice_tracking
                              : currentSegment === "field-mapping"
                                ? labels.excel_schema_map
                                : currentSegment === "booking-dashboard"
                                  ? "Booking Dashboard"
                                  : currentSegment === "pricing"
                                    ? labels.pricing
                                    : location.pathname.includes(BASE_PATH)
                                      ? labels.my_tasks
                                      : "";

  return (
    <Header {...{ showSidebar, selectedItem, selectedSubItem }}>
      {children}
    </Header>
  );
}
